'use client';

import React, { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import styles from './Dialog.module.css';

interface DialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
  onBeforeClose?: () => Promise<boolean> | boolean;
  footer?: React.ReactNode;
  showCloseButton?: boolean;
}

const Dialog: React.FC<DialogProps> = ({
  isOpen,
  onClose,
  title,
  children,
  size = 'medium',
  closeOnOverlayClick = true,
  closeOnEscape = true,
  onBeforeClose,
  footer,
  showCloseButton = true,
}) => {
  const handleClose = useCallback(async () => {
    if (onBeforeClose) {
      const canClose = await Promise.resolve(onBeforeClose());
      if (!canClose) return;
    }
    onClose();
  }, [onClose, onBeforeClose]);

  // Handle overlay click
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target !== e.currentTarget || !closeOnOverlayClick) return;
    handleClose();
  };

  // Handle escape key
  useEffect(() => {
    if (!isOpen || !closeOnEscape) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, closeOnEscape, onClose]);

  // Handle body scroll lock
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return createPortal(
    <div className={styles.overlay} onClick={handleOverlayClick}>
      <div className={`${styles.dialog} ${styles[size]}`}>
        {(title || closeOnOverlayClick) && (
          <div className={styles.header}>
            {title && <h2 className={styles.title}>{title}</h2>}
            <button
              className={styles.closeButton}
              onClick={onClose}
              aria-label="Close dialog"
              type="button"
            >
              ×
            </button>
          </div>
        )}
        <div className={styles.content}>
          {children}
        </div>
        {footer && (
          <div className={styles.footer}>
            {footer}
          </div>
        )}
        {showCloseButton && !footer && (
          <div className={styles.footer}>
            <button
              onClick={handleClose}
              className={styles.closeFooterButton}
              aria-label="Close dialog"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Dialog;
